import * as assert from 'assert';
import * as firebase from 'firebase';
import { computed, observable } from 'mobx';
import { IUserInfo } from '../models/User';
import { AccountInfo, UserService } from '../services/UserService';
import { ISetupApplicationStore } from './ApplicationStore';
import User = firebase.User;

const config = {
    apiKey: "AIzaSyAzP_hhTHWMs1UKD54zQ_pKXODnRHYNA7k",
    authDomain: "nl-body-knowledge.firebaseapp.com",
    databaseURL: "https://nl-body-knowledge.firebaseio.com/",
    storageBucket: "gs://nl-body-knowledge.appspot.com",
};

firebase.initializeApp(config);

export class AuthorizationStore {
    private _applicationStore: ISetupApplicationStore;
    @observable private _isAuthenticated: boolean;
    @observable private _autoLogOnComplete: boolean = false;
    @observable private _firebaseUser?: User;
    @observable private _userInfo?: IUserInfo;
    @observable private _token?: string;

    constructor(applicationStore: ISetupApplicationStore) {
        this._isAuthenticated = false;
        this._applicationStore = applicationStore;
        this.startAuthListener();
    }

    @computed
    public get isAuthenticated(): boolean {
        return this._isAuthenticated;
    }

    @computed
    public get autoLogOnComplete(): boolean {
        return this._autoLogOnComplete;
    }

    @computed
    public get firebaseUser() {
        assert(null != this._firebaseUser, 'called get firebaseUser before it was available');
        if (this._firebaseUser) {
            return this._firebaseUser;
        } else {
            throw new Error('called get firebaseUser before it was available');
        }
    }

    @computed
    public get userProfile(): IUserInfo {
        assert(null != this._userInfo, 'called get userProfile before it was available');
        if (this._userInfo) {
            return this._userInfo;
        } else {
            throw new Error('called get userProfile before it was available');
        }
    }

    @computed
    public get authToken(): string {
        assert(null != this._token, 'called get authToken before it was available');
        if (this._token) {
            return this._token;
        } else {
            throw new Error('called get authToken before it was available');
        }
    }

    public startAuthListener(): void {
        firebase.auth().onAuthStateChanged(
            (authUser: firebase.User | null) => {
                this._authCallback();
            },
            (error: firebase.auth.Error) => {
                this.logout();
                this._autoLogOnComplete = true;
            },
            () => {
                this._autoLogOnComplete = true;
            }
        );
    }

    private async _authCallback(): Promise<void> {
        this._firebaseUser = firebase.auth().currentUser || undefined;
        if (undefined != this._firebaseUser) {
            return this._handleAuthenticatedUser();
        } else {
            return this.logout();
        }
    }

    private _handleAuthenticatedUser(): void {
        this.getAuthToken()
            .then(token => {
                this._token = token;
                const userService = new UserService(token);
                return userService.checkAccount();
            })
            .then((account: AccountInfo) => {
                if (false === account.is_ready) {
                    throw new Error('You must set up an account before using the web portal');
                }
                this._userInfo = account.user_info;
                this._isAuthenticated = true;
                this._applicationStore.initializeStore();
                this._autoLogOnComplete = true;
            })
            .catch(err => {
                // TODO: show error message to user
                this.logout();
            });
    }

    private getAuthToken(): Promise<any> {
        if (firebase && firebase.auth() && null != firebase.auth().currentUser) {
            return firebase.auth().currentUser!.getIdToken(true);
        }
        return Promise.resolve();
    }

    public login(user_name: string, password: string): Promise<void> {
        return firebase.auth().signInWithEmailAndPassword(user_name, password)
            .then(() => {
                return this._authCallback();
            });
    }

    public googleSignIn(): Promise<void> {
        const provider = new firebase.auth.GoogleAuthProvider();
        return firebase.auth().signInWithPopup(provider)
            .then(() => {
                return this._authCallback();
            });
    }

    public facebookSignIn(): Promise<void> {
        const provider = new firebase.auth.FacebookAuthProvider();
        return firebase.auth().signInWithPopup(provider)
            .then(() => {
                return this._authCallback();
            });
    }

    public logout(): void {
        this._applicationStore.unInitialize();
        this._isAuthenticated = false;
        this._autoLogOnComplete = true;
        this._firebaseUser = undefined;
        this._userInfo = undefined;
        firebase.auth().signOut();
    }

    public resetPassword(email: string): void {
        firebase.auth().sendPasswordResetEmail(email);
    }

}




// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/data_layer/stores/AuthorizationStore.ts